import React from 'react';

/**
 * Shared stroke-style icon set — same visual language as the Welcome modal
 * tiles (thin 1.5px strokes, rounded caps, currentColor).
 */
const Icon = ({ size = 18, children, ...props }) => (
  <svg
    viewBox="0 0 26 26"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    {children}
  </svg>
);

/**
 * Shape data for the icons that must also exist as raw markup: the print sheet
 * (App.js) and the node-description renderer build HTML strings and cannot use
 * JSX. Declaring the geometry once stops the two renderings from drifting.
 */
const ICON_SHAPES = {
  clipboard: [
    ['rect', { x: '5.5', y: '5', width: '15', height: '17', rx: '2' }],
    ['path', { d: 'M9.5 5a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1.5h-7V5Z' }],
    ['path', { d: 'M9 11.5h8M9 15h8M9 18.5h5' }],
  ],
  stethoscope: [
    ['path', { d: 'M5.5 4.5v5a5 5 0 0 0 10 0v-5' }],
    ['path', { d: 'M4.5 4.5h2M15.5 4.5h-2' }],
    ['path', { d: 'M10.5 14.5v2.5a5 5 0 0 0 10 0v-2' }],
    ['circle', { cx: '20.5', cy: '12.5', r: '2' }],
  ],
  hourglass: [
    ['path', { d: 'M7 3.5h12M7 22.5h12' }],
    ['path', { d: 'M8.5 3.5v3.2c0 2.6 2 4.3 4.5 6.3-2.5 2-4.5 3.7-4.5 6.3v3.2M17.5 3.5v3.2c0 2.6-2 4.3-4.5 6.3 2.5 2 4.5 3.7 4.5 6.3v3.2' }],
  ],
  books: [
    ['rect', { x: '4', y: '4', width: '4.5', height: '18', rx: '1' }],
    ['rect', { x: '10', y: '7', width: '4.5', height: '15', rx: '1' }],
    ['path', { d: 'm15.8 8.3 4.3-1.1 3 14.5-4.3 1.1-3-14.5Z' }],
  ],
  warning: [
    ['path', { d: 'M13 4 23 21H3L13 4Z' }],
    ['path', { d: 'M13 10.5v5' }],
    ['path', { d: 'M13 18.5h.01', strokeWidth: '2' }],
  ],
};

const renderShapes = (name) =>
  ICON_SHAPES[name].map(([tag, attrs], i) => React.createElement(tag, { key: i, ...attrs }));

const attrsToMarkup = (attrs) =>
  Object.entries(attrs)
    .map(([key, value]) => `${key.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase())}="${value}"`)
    .join(' ');

/**
 * Any ICON_SHAPES entry as an HTML string, for contexts that build markup by
 * hand. Uses currentColor like the components, so it takes the surrounding text
 * colour rather than an emoji's baked-in one.
 */
export const iconMarkup = (name, size = 14) =>
  `<svg viewBox="0 0 26 26" width="${size}" height="${size}" fill="none" stroke="currentColor"` +
  ` stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"` +
  ` style="vertical-align:-2px;flex-shrink:0">` +
  ICON_SHAPES[name].map(([tag, attrs]) => `<${tag} ${attrsToMarkup(attrs)}/>`).join('') +
  `</svg>`;

// Globe — language switcher
export const IconGlobe = (props) => (
  <Icon {...props}>
    <circle cx="13" cy="13" r="9" />
    <ellipse cx="13" cy="13" rx="4" ry="9" />
    <path d="M4.5 10h17M4.5 16h17" />
  </Icon>
);

// Info circle — Welcome button
export const IconInfo = (props) => (
  <Icon {...props}>
    <circle cx="13" cy="13" r="9" />
    <path d="M13 12v5.5" />
    <path d="M13 8.5h.01" strokeWidth="2" />
  </Icon>
);

// Open book — My Story button
export const IconBook = (props) => (
  <Icon {...props}>
    <path d="M13 6.5C11.2 5 8.6 4.5 5 4.5v15c3.6 0 6.2.5 8 2 1.8-1.5 4.4-2 8-2v-15c-3.6 0-6.2.5-8 2Z" />
    <path d="M13 6.5v15" />
  </Icon>
);

// Magnifier — search
export const IconSearch = (props) => (
  <Icon {...props}>
    <circle cx="11.5" cy="11.5" r="6.5" />
    <path d="m16.5 16.5 5 5" />
  </Icon>
);

// Expand corners — overview / fit view
export const IconOverview = (props) => (
  <Icon {...props}>
    <path d="M4 9V5.5A1.5 1.5 0 0 1 5.5 4H9" />
    <path d="M17 4h3.5A1.5 1.5 0 0 1 22 5.5V9" />
    <path d="M22 17v3.5a1.5 1.5 0 0 1-1.5 1.5H17" />
    <path d="M9 22H5.5A1.5 1.5 0 0 1 4 20.5V17" />
    <circle cx="13" cy="13" r="3" />
  </Icon>
);

// Target — focus on selected
export const IconTarget = (props) => (
  <Icon {...props}>
    <circle cx="13" cy="13" r="9" />
    <circle cx="13" cy="13" r="5" />
    <path d="M13 13h.01" strokeWidth="2.5" />
  </Icon>
);

// Lightbulb — my ideas
export const IconBulb = (props) => (
  <Icon {...props}>
    <path d="M13 3.5a6 6 0 0 0-3.5 10.9c.65.45 1 1.1 1 1.85v1.1h5v-1.1c0-.75.35-1.4 1-1.85A6 6 0 0 0 13 3.5Z" />
    <path d="M10.6 19.9h4.8M11.1 22.4h3.8" />
  </Icon>
);

// Headphones — music
export const IconHeadphones = (props) => (
  <Icon {...props}>
    <path d="M4.5 17v-3a8.5 8.5 0 0 1 17 0v3" />
    <rect x="4.5" y="15" width="4.5" height="6.5" rx="2" />
    <rect x="17" y="15" width="4.5" height="6.5" rx="2" />
  </Icon>
);

// Crescent moon — premium
export const IconMoon = (props) => (
  <Icon {...props}>
    <path d="M20.5 15.5A8.5 8.5 0 0 1 10.5 5.5 8.5 8.5 0 1 0 20.5 15.5Z" />
    <path d="M17.5 5.5h.01M20 8.5h.01" strokeWidth="2" />
  </Icon>
);

// Star — favorites
export const IconStar = (props) => (
  <Icon {...props}>
    <path d="m13 3.8 2.7 5.6 6.1.8-4.5 4.3 1.1 6-5.4-2.9-5.4 2.9 1.1-6L4.2 10.2l6.1-.8L13 3.8Z" />
  </Icon>
);

// Printer — print actions
export const IconPrinter = (props) => (
  <Icon {...props}>
    <path d="M7.5 9V4.5h11V9" />
    <path d="M7.5 17.5h-2A1.5 1.5 0 0 1 4 16v-5.5A1.5 1.5 0 0 1 5.5 9h15A1.5 1.5 0 0 1 22 10.5V16a1.5 1.5 0 0 1-1.5 1.5h-2" />
    <rect x="7.5" y="14.5" width="11" height="7" rx="1" />
    <path d="M18.5 12h.01" strokeWidth="2" />
  </Icon>
);

// Check mark — completed
export const IconCheck = (props) => (
  <Icon {...props}>
    <path d="m5 13.5 5 5L21 7.5" />
  </Icon>
);

// Cross — excluded
export const IconCross = (props) => (
  <Icon {...props}>
    <path d="M6.5 6.5l13 13M19.5 6.5l-13 13" />
  </Icon>
);

// Clipboard — information / description
export const IconClipboard = (props) => (
  <Icon {...props}>{renderShapes('clipboard')}</Icon>
);

// Stethoscope — who can help
export const IconStethoscope = (props) => (
  <Icon {...props}>{renderShapes('stethoscope')}</Icon>
);

// Hourglass — trial duration
export const IconHourglass = (props) => (
  <Icon {...props}>{renderShapes('hourglass')}</Icon>
);

// Stacked books — sources
export const IconBooks = (props) => (
  <Icon {...props}>{renderShapes('books')}</Icon>
);

// Warning triangle — AI-generated notice
export const IconWarning = (props) => (
  <Icon {...props}>{renderShapes('warning')}</Icon>
);

// Envelope — contact
export const IconMail = (props) => (
  <Icon {...props}>
    <rect x="3.5" y="6" width="19" height="14" rx="2" />
    <path d="m4.5 7.5 8.5 7 8.5-7" />
  </Icon>
);

// Heart — support
export const IconHeart = (props) => (
  <Icon {...props}>
    <path d="M13 20.5S4.5 15.4 4.5 9.9A4.4 4.4 0 0 1 13 8a4.4 4.4 0 0 1 8.5 1.9c0 5.5-8.5 10.6-8.5 10.6Z" />
  </Icon>
);

// Padlock — account required
export const IconLock = (props) => (
  <Icon {...props}>
    <rect x="5.5" y="11" width="15" height="10.5" rx="2" />
    <path d="M9 11V8a4 4 0 0 1 8 0v3" />
    <path d="M13 15.5v2" />
  </Icon>
);

// Eye — tap-to-preview on combo circles (mobile)
export const IconEye = (props) => (
  <Icon {...props}>
    <path d="M2.5 13S6.5 6.5 13 6.5 23.5 13 23.5 13 19.5 19.5 13 19.5 2.5 13 2.5 13Z" />
    <circle cx="13" cy="13" r="3" />
  </Icon>
);

// Two people — community
export const IconCommunity = (props) => (
  <Icon {...props}>
    <circle cx="9.5" cy="9" r="3.5" />
    <path d="M3.5 21c.5-3.7 2.9-5.5 6-5.5s5.5 1.8 6 5.5" />
    <circle cx="17.5" cy="10" r="2.8" />
    <path d="M16.5 15.6c2.9.1 5.4 1.7 6 5" />
  </Icon>
);

// Chevron — expand/collapse toggle
export const IconChevron = (props) => (
  <Icon {...props}>
    <path d="M6.5 10 13 16.5 19.5 10" />
  </Icon>
);

// Sparkle — AI-generated content
export const IconSparkle = (props) => (
  <Icon {...props}>
    <path d="M13 3c.6 3.7 1.7 6.1 3.4 7.6C18.1 12.1 20.5 13.2 24 13.8c-3.5.6-5.9 1.7-7.6 3.4-1.7 1.5-2.8 3.9-3.4 7.6-.6-3.7-1.7-6.1-3.4-7.6C7.9 15.5 5.5 14.4 2 13.8c3.5-.6 5.9-1.7 7.6-3.4C11.3 9.1 12.4 6.7 13 3Z" />
  </Icon>
);

/* ── Inline HTML-string icons ────────────────────────────────────────────────
   For content rendered via dangerouslySetInnerHTML (e.g. the My Story text,
   which lives in the database with emoji markers). Same stroke style as the
   components above. */
const inlineSvg = (inner) =>
  `<svg viewBox="0 0 26 26" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="vertical-align:-0.125em;margin-right:3px">${inner}</svg>`;

const INLINE_ICONS = {
  moon: inlineSvg('<path d="M20.5 15.5A8.5 8.5 0 0 1 10.5 5.5 8.5 8.5 0 1 0 20.5 15.5Z"/><path d="M17.5 5.5h.01M20 8.5h.01" stroke-width="2"/>'),
  bulb: inlineSvg('<path d="M13 3.5a6 6 0 0 0-3.5 10.9c.65.45 1 1.1 1 1.85v1.1h5v-1.1c0-.75.35-1.4 1-1.85A6 6 0 0 0 13 3.5Z"/><path d="M10.6 19.9h4.8M11.1 22.4h3.8"/>'),
  warning: inlineSvg('<path d="M13 4 23 21H3L13 4Z"/><path d="M13 10.5v5"/><path d="M13 18.5h.01" stroke-width="2"/>'),
  search: inlineSvg('<circle cx="11.5" cy="11.5" r="6.5"/><path d="m16.5 16.5 5 5"/>'),
  calendar: inlineSvg('<rect x="4" y="6" width="18" height="15.5" rx="2"/><path d="M4 10.5h18M9 3.5v4M17 3.5v4"/>'),
  pill: inlineSvg('<rect x="4.5" y="9.5" width="17" height="7" rx="3.5" transform="rotate(-45 13 13)"/><path d="m9.5 16.5 7-7"/>'),
  battery: inlineSvg('<rect x="3.5" y="8.5" width="17" height="9" rx="2"/><path d="M23 11.5v3"/><path d="M7 11.5v3"/>'),
  map: inlineSvg('<path d="M9 4.5 3.5 6.5v15L9 19.5l8 2 5.5-2v-15L17 6.5l-8-2Z"/><path d="M9 4.5v15M17 6.5v15"/>'),
  tree: inlineSvg('<path d="M13 3.5 7.5 11h2.5L5.5 17.5h15L16 11h2.5L13 3.5Z"/><path d="M13 17.5v5"/>'),
};

// Story emoji markers → site icons ('' = decorative, just removed)
const EMOJI_TO_ICON = {
  '🛌': INLINE_ICONS.moon,
  '🌛': '',
  '🌒': INLINE_ICONS.moon,
  '🤯': INLINE_ICONS.bulb,
  '🧠': INLINE_ICONS.bulb,
  '📅': INLINE_ICONS.calendar,
  '💥': INLINE_ICONS.warning,
  '💊': INLINE_ICONS.pill,
  '🪫': INLINE_ICONS.battery,
  '👨‍🔬': INLINE_ICONS.search,
  '🗺️': INLINE_ICONS.map,
  '🗺': INLINE_ICONS.map,
  '🌳': INLINE_ICONS.tree,
};

// Replace known emoji markers with inline SVG icons; strip trailing spaces
// left behind when an emoji maps to nothing.
export const replaceEmojisWithIcons = (text) => {
  if (!text) return text;
  let out = text;
  for (const [emoji, svg] of Object.entries(EMOJI_TO_ICON)) {
    out = out.split(`${emoji} `).join(svg ? `${svg} ` : '');
    out = out.split(emoji).join(svg);
  }
  return out;
};
